import Report from '@xpeng/gio-report'
import { getClientEnv } from './common'

/**
 * GrowingIO（鼹鼠）埋点封装
 *
 * SDK: @xpeng/gio-report。用法：
 *   reportGio({ type: 'event', eventId: 'APP_P20383_B001', journeyOrderNo, title })
 * - eventId 形如 {前缀}_{pageId}_{buttonId}，pageId/buttonId 由 SDK 从 eventId 自动拆解；
 * - 业务属性（journeyOrderNo/title/duration 等）以平铺 key 传入，会进入事件 attributes；
 * - 不把 appid/so/ch 等连接配置作为公共属性传入，保持 attributes 干净（对齐图1）。
 *
 * ⚠️ dataSourceId 来自参考项目 xp-app-h5-v2（小鹏APP售后 H5 的 GrowingIO 数据源）。
 *    若本项目为独立数据源，请替换为本项目在 GrowingIO 平台申请的 dataSourceId。
 */
const DATA_SOURCE_ID = {
  prod: 'bb201627a61ab056',
  test: '82cedf17ef3530eb',
}

// 模块级单例，确保整个应用生命周期只初始化一次
let gioReporter: any = null

/** 初始化 GrowingIO Report 单例 */
function initGioReporter() {
  if (gioReporter) return gioReporter
  if (typeof window === 'undefined') return null
  try {
    const isProd = getClientEnv() === 'production'
    gioReporter = new Report(
      // 第一个参数是「每条事件都携带的公共属性」，会进入事件 attributes。
      // 这里保持为空，避免把 appid/so/ch 等连接配置污染进 attributes（对齐图1的干净写法）。
      {},
      {
        autotrack: false,
        env: isProd ? 'prod' : 'test',
        // 数据源由 dataSourceId 路由（GIO 采集 token），无需再把 appid 作为公共属性传入
        dataSourceId: isProd ? DATA_SOURCE_ID.prod : DATA_SOURCE_ID.test,
      }
    )
  } catch (error) {
    console.error('[gioReport] Report 初始化失败:', error)
    gioReporter = null
  }
  return gioReporter
}

// 模块加载时初始化
initGioReporter()

export interface GioReportData {
  /** 事件ID，格式 {前缀}_{pageId}_{buttonId}，如 APP_P20383_B001（pageId/buttonId 会据此拆出） */
  eventId: string
  /** 行为类型：CLICK-点击，VIEW-曝光/浏览 */
  type?: 'CLICK' | 'VIEW'
  /** 事件可读名称 */
  name?: string
  /** 事件描述 */
  description?: string
  /** 内容描述：点击/停留了什么，如「点击了服务记录页面的车辆报告」 */
  content_type?: string
  /** 停留时长（毫秒），仅停留时长类事件传 */
  duration?: number
  /** 视频播放时长（秒，字符串），仅视频播放埋点传 */
  playtime?: string
  /** 用户ID（能拿到才传，拿不到不写） */
  user_id?: string
  /** 页面标识（能拿到才传，拿不到不写） */
  pageFlag?: string
}

/**
 * 发送 GrowingIO（鼹鼠）埋点
 *
 * attributes 严格按参考模板字段组装：pageFlag/user_id/page_id/button_id/path/name/description/referrer/type。
 * - page_id/button_id 由 eventId 拆出；path/referrer 自动取；其余由调用方传入。
 * - 空值一律不写，不输出模板以外的无关字段。
 * - 员工/门店等字段（employee_id/emp_code/org_* 等）消费端拿不到，故不写。
 *
 * @example reportGio({ eventId: 'APP_P20383_B001', type: 'CLICK', name: '点击查看说明' })
 */
export function reportGio(data: GioReportData) {
  const reporter = initGioReporter()
  if (!reporter) return

  const { eventId, type, name, description, content_type, duration, playtime, user_id, pageFlag } = data

  // 从 eventId 拆出 page_id / button_id：APP_P20383_B001 → page_id=P20383, button_id=B001
  const segs = eventId.split('_')
  const button_id = segs[segs.length - 1] || ''
  const page_id = segs[segs.length - 2] || ''
  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  const referrer = typeof document !== 'undefined' ? document.referrer : ''

  // 严格按参考模板字段组装，空值不写、不加无关字段
  const attributes: Record<string, string | number> = {}
  if (pageFlag) attributes.pageFlag = pageFlag
  if (user_id) attributes.user_id = user_id
  if (page_id) attributes.page_id = page_id
  if (button_id) attributes.button_id = button_id
  if (path) attributes.path = path
  if (name) attributes.name = name
  if (description) attributes.description = description
  if (content_type) attributes.content_type = content_type
  if (referrer) attributes.referrer = referrer
  if (type) attributes.type = type
  if (duration != null) attributes.duration = duration
  if (playtime != null) attributes.playtime = playtime

  // eventId 由 SDK 解析为 eventName，其余平铺字段进入 attributes
  reporter.send({ eventId, ...attributes })
}
